import React from 'react';

import { Link, Stack, useTheme } from '@mui/material';
import { CallCalling, Link1, SmsTracking } from 'iconsax-reactjs';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router';

import { IUser } from '../../../../../../types/user.type';
import { ContactLabelStyled, ContactStyled, IconWrapperStyled } from '../../styles';

interface IProps {
  user: IUser;
}

const UserInfoContacts = React.memo(({ user }: IProps) => {
  const { t } = useTranslation();

  const theme = useTheme();

  const linkedin = React.useMemo(() => {
    if (!user.linkedin) {
      return null;
    }

    const username = user.linkedin
      .replace(/https:\/\/(www\.)?linkedin\.com\/in\//, '')
      .replace(/\/$/, '');

    return `@${username}`;
  }, [user.linkedin]);

  return (
    <Stack
      spacing={2}
      sx={{
        paddingBottom: theme.spacing(1.5),
        borderBottom: `1px solid ${theme.palette.secondary[100]}`,
      }}
    >
      <Stack direction="row" spacing={1.5}>
        <IconWrapperStyled>
          <CallCalling size={16} color={theme.palette.primary.main} />
        </IconWrapperStyled>

        <Stack spacing={0.5}>
          <ContactLabelStyled variant="sm">{t('general.phone')}</ContactLabelStyled>
          <ContactStyled variant="md">{user.phone}</ContactStyled>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1.5}>
        <IconWrapperStyled sx={{ backgroundColor: theme.palette.green[100] }}>
          <SmsTracking size={16} color={theme.palette.green[900]} />
        </IconWrapperStyled>

        <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
          <ContactLabelStyled variant="sm">{t('general.email')}</ContactLabelStyled>
          <ContactStyled variant="md">{user.email}</ContactStyled>
        </Stack>
      </Stack>

      {user.linkedin && (
        <Stack direction="row" spacing={1.5}>
          <IconWrapperStyled sx={{ backgroundColor: theme.palette.violet[100] }}>
            <Link1 size={16} color={theme.palette.violet[900]} />
          </IconWrapperStyled>

          <Stack spacing={0.5}>
            <ContactLabelStyled variant="sm">{t('general.linkedin')}</ContactLabelStyled>

            <Link
              component={RouterLink}
              to={user.linkedin}
              target="_blank"
              rel="nofollow"
              sx={{
                textDecoration: 'none',
                ':hover': {
                  textDecoration: 'underline',
                  textDecorationColor: theme.palette.grey[900],
                },
              }}
            >
              <ContactStyled variant="md">{linkedin}</ContactStyled>
            </Link>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
});

export default UserInfoContacts;
